import { useLocation, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/components/ui';

/*
  CanvasHeader — тонкая полоса вверху canvas с breadcrumb (variant C
  из обсуждения 2026-05-21). Search НЕ здесь — он остаётся floating dock.

  Сейчас простой URL-based breadcrumb. Когда дойдём до drill-down в
  secondary с накоплением состояния — breadcrumb будет читать tab/drill
  state из глобального стора.

  Высота 48px. Sticky к верху canvas'а (scroll внутри content не двигает).
*/

interface BreadcrumbSegment {
  label: string;
  to?: string; // если есть — кликабельно
}

export function CanvasHeader() {
  const location = useLocation();
  const segments = breadcrumbForPath(location.pathname);

  return (
    <header
      className={cn(
        'sticky top-0 z-raised flex shrink-0 items-center gap-2 px-6 py-3',
        'border-b border-line bg-canvas/95 backdrop-blur-sm',
      )}
    >
      <nav className="flex items-center gap-1.5 text-sm" aria-label="Breadcrumb">
        {segments.map((s, i) => (
          <Segment key={i} seg={s} last={i === segments.length - 1} />
        ))}
      </nav>
    </header>
  );
}

function Segment({ seg, last }: { seg: BreadcrumbSegment; last: boolean }) {
  const content = (
    <span
      className={cn(
        'transition-colors duration-fast',
        last ? 'font-semibold text-fg' : 'text-fg-subtle hover:text-fg',
      )}
    >
      {seg.label}
    </span>
  );
  return (
    <>
      {seg.to ? (
        <Link to={seg.to} viewTransition>
          {content}
        </Link>
      ) : (
        content
      )}
      {!last && <ChevronRight size={14} className="shrink-0 text-fg-subtle" />}
    </>
  );
}

// ----- URL → breadcrumb -----

function breadcrumbForPath(path: string): BreadcrumbSegment[] {
  // Home
  if (path === '/') return [{ label: 'Главная' }];

  if (path.startsWith('/marketplace')) {
    return [{ label: 'Marketplace' }];
  }
  if (path === '/portfolio/me') {
    return [{ label: 'Моё портфолио' }];
  }
  if (path.startsWith('/portfolio/')) {
    return [{ label: 'Портфолио', to: '/portfolio/me' }, { label: path.split('/').pop() ?? 'Юзер' }];
  }
  if (path === '/settings') {
    return [{ label: 'Настройки' }];
  }
  if (path.startsWith('/settings/')) {
    const sub = path.split('/').pop();
    const map: Record<string, string> = {
      profile: 'Профиль',
      notifications: 'Уведомления',
      integrations: 'Интеграции',
      security: 'Безопасность',
    };
    return [{ label: 'Настройки', to: '/settings' }, { label: (sub && map[sub]) || sub || '' }];
  }

  // Fallback — показываем path-сегменты
  const parts = path.split('/').filter(Boolean);
  return parts.map((p) => ({ label: p }));
}
